import React, { useCallback, useEffect, useMemo, useState } from "react";
import { AxiosError } from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "../utils/toast";
import AdminWorkspaceNav from "../components/admin/AdminWorkspaceNav";
import { useAuth } from "../contexts/AuthContext";
import {
  deleteAdminOrder,
  fetchAdminOrders,
  updateAdminOrderStatus,
} from "../services/adminService";
import { Order } from "../types/store";
import "../styles/pages/AdminDashboardPage.css";

const ORDER_STATUS_OPTIONS = [
  "CONFIRMED",
  "PROCESSING",
  "SHIPPED",
  "OUT_FOR_DELIVERY",
  "DELIVERED",
  "CANCELLED",
];

const STORE_PICKUP_STATUS_OPTIONS = ["CONFIRMED", "DELIVERED", "CANCELLED"];

const formatOrderStatus = (status: string) =>
  status
    .toLowerCase()
    .replace(/_/g, " ")
    .replace(/\b\w/g, (character) => character.toUpperCase());

const formatCurrency = (value: number) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(value);

const formatDateTime = (value: string) =>
  new Date(value).toLocaleString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });

const extractErrorMessage = (error: unknown, fallback: string) => {
  if (error instanceof AxiosError) {
    const message = error.response?.data?.message;
    if (typeof message === "string" && message.trim()) {
      return message;
    }
  }

  if (error instanceof Error && error.message.trim()) {
    return error.message;
  }

  return fallback;
};

const AdminPendingOrdersPage: React.FC = () => {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusUpdatingId, setStatusUpdatingId] = useState<number | null>(null);
  const [deletingOrderId, setDeletingOrderId] = useState<number | null>(null);
  const [orderStatusDrafts, setOrderStatusDrafts] = useState<Record<number, string>>({});
  const [expandedOrderId, setExpandedOrderId] = useState<number | null>(null);

  const handleAdminRequestError = useCallback(
    (error: unknown, fallback: string) => {
      if (error instanceof AxiosError && error.response?.status === 401) {
        toast.error("Your admin session has expired. Please log in again.");
        logout();
        navigate("/admin/login", {
          replace: true,
          state: { from: location, adminOnly: true },
        });
        return;
      }

      if (error instanceof AxiosError && error.response?.status === 403) {
        toast.error("You do not have permission to manage orders.");
        return;
      }

      toast.error(extractErrorMessage(error, fallback));
    },
    [location, logout, navigate],
  );

  const loadOrders = useCallback(async () => {
    setLoading(true);
    try {
      const orderResponse = await fetchAdminOrders();
      setOrders(orderResponse);
      setOrderStatusDrafts((current) => {
        const nextDrafts = { ...current };
        orderResponse.forEach((order) => {
          if (nextDrafts[order.id] == null) {
            nextDrafts[order.id] = order.status;
          }
        });
        return nextDrafts;
      });
    } catch (error) {
      handleAdminRequestError(error, "Unable to load pending orders.");
    } finally {
      setLoading(false);
    }
  }, [handleAdminRequestError]);

  useEffect(() => {
    void loadOrders();
  }, [loadOrders]);

  const pendingOrders = useMemo(
    () => orders.filter((order) => order.status !== "DELIVERED"),
    [orders],
  );

  const pendingCountByStatus = useMemo(() => {
    const counts: Record<string, number> = {};
    pendingOrders.forEach((order) => {
      counts[order.status] = (counts[order.status] || 0) + 1;
    });
    return counts;
  }, [pendingOrders]);

  const handleStatusChange = async (orderId: number, status: string) => {
    setStatusUpdatingId(orderId);
    try {
      const updated = await updateAdminOrderStatus(orderId, status);
      setOrders((current) => current.map((order) => (order.id === orderId ? updated : order)));
      setOrderStatusDrafts((current) => ({ ...current, [orderId]: status }));
      toast.success(`Order marked as ${formatOrderStatus(status)}.`);
      await loadOrders();
    } catch (error) {
      handleAdminRequestError(error, "Unable to update the order status.");
    } finally {
      setStatusUpdatingId(null);
    }
  };

  const handleDeleteOrder = async (orderId: number) => {
    setDeletingOrderId(orderId);
    try {
      await deleteAdminOrder(orderId);
      toast.success("Order deleted.");
      setOrders((current) => current.filter((order) => order.id !== orderId));
      setOrderStatusDrafts((current) => {
        const nextDrafts = { ...current };
        delete nextDrafts[orderId];
        return nextDrafts;
      });
    } catch (error) {
      handleAdminRequestError(error, "Unable to delete the order.");
    } finally {
      setDeletingOrderId(null);
    }
  };

  return (
    <section className="shell section page-section">
      <div className="page-header admin-page-header">
        <div>
          <span className="eyebrow">Admin workspace</span>
          <h1>Pending orders</h1>
        </div>
      </div>
      <AdminWorkspaceNav />

      <section className="store-card admin-panel admin-panel--full">
        <div className="admin-panel__heading">
          <div>
            <span className="eyebrow">Realtime queue</span>
            <h2>Orders waiting for delivery completion</h2>
          </div>
        </div>
        <div className="admin-today-analytics" style={{ marginBottom: 18 }}>
          {Object.entries(pendingCountByStatus).length ? (
            Object.entries(pendingCountByStatus).map(([status, count]) => (
              <div key={status} className="admin-today-analytics__card">
                <span>{formatOrderStatus(status)}</span>
                <strong>{count}</strong>
                <small>Still pending until delivered</small>
              </div>
            ))
          ) : (
            <div className="admin-today-analytics__card">
              <span>Pending orders</span>
              <strong>0</strong>
              <small>No active queue right now.</small>
            </div>
          )}
        </div>

        <div className="admin-table">
          <table>
            <thead>
              <tr>
                <th>Order</th>
                <th>Customer</th>
                <th>Mode</th>
                <th>Placed</th>
                <th>Total</th>
                <th>Status</th>
                <th>Update</th>
                <th>Delete</th>
              </tr>
            </thead>
            <tbody>
              {!loading && pendingOrders.length ? (
                pendingOrders.map((order) => (
                  <React.Fragment key={order.id}>
                    <tr>
                      <td>
                        <button
                          className="admin-order-toggle"
                          type="button"
                          onClick={() =>
                            setExpandedOrderId((current) => (current === order.id ? null : order.id))
                          }
                        >
                          <strong>{order.orderNumber}</strong>
                          <span>{expandedOrderId === order.id ? "Hide details" : "Show details"}</span>
                        </button>
                        <div>{order.items.length} item(s)</div>
                      </td>
                      <td>
                        <strong>{order.shippingName}</strong>
                        <div>{order.email}</div>
                      </td>
                      <td>{order.deliveryMode === "STORE_PICKUP" ? "Store pickup" : "Home delivery"}</td>
                      <td>{formatDateTime(order.createdAt)}</td>
                      <td>{formatCurrency(order.totalAmount)}</td>
                      <td>
                        <span className="admin-status-pill">{formatOrderStatus(order.status)}</span>
                      </td>
                      <td>
                        <div className="admin-order-status-control">
                          <select
                            value={orderStatusDrafts[order.id] ?? order.status}
                            disabled={statusUpdatingId === order.id}
                            onChange={(event) =>
                              setOrderStatusDrafts((current) => ({
                                ...current,
                                [order.id]: event.target.value,
                              }))
                            }
                          >
                            {(order.deliveryMode === "STORE_PICKUP"
                              ? STORE_PICKUP_STATUS_OPTIONS
                              : ORDER_STATUS_OPTIONS
                            ).map((status) => (
                              <option key={status} value={status}>
                                {formatOrderStatus(status)}
                              </option>
                            ))}
                          </select>
                          <button
                            className="button button--light"
                            type="button"
                            disabled={statusUpdatingId === order.id}
                            onClick={() =>
                              void handleStatusChange(order.id, orderStatusDrafts[order.id] ?? order.status)
                            }
                          >
                            {statusUpdatingId === order.id ? (
                              <span className="button-loading button-loading--dark">
                                <span className="button-loading__spinner" aria-hidden="true" />
                                Updating...
                              </span>
                            ) : (
                              "Update"
                            )}
                          </button>
                        </div>
                      </td>
                      <td>
                        <button
                          className="link-button admin-danger-button"
                          type="button"
                          disabled={deletingOrderId === order.id}
                          onClick={() => void handleDeleteOrder(order.id)}
                        >
                          {deletingOrderId === order.id ? (
                            <span className="button-loading">
                              <span className="button-loading__spinner" aria-hidden="true" />
                              Deleting...
                            </span>
                          ) : (
                            "Delete"
                          )}
                        </button>
                      </td>
                    </tr>
                    {expandedOrderId === order.id ? (
                      <tr className="admin-order-details-row">
                        <td colSpan={8}>
                          <div className="admin-order-details">
                            <section className="admin-order-details__section">
                              <span className="eyebrow">User details</span>
                              <strong>{order.shippingName}</strong>
                              <p>{order.email}</p>
                              <p>{order.phone}</p>
                            </section>
                            <section className="admin-order-details__section">
                              <span className="eyebrow">Order details</span>
                              <p>Order no: {order.orderNumber}</p>
                              <p>Status: {order.status}</p>
                              <p>Placed: {formatDateTime(order.createdAt)}</p>
                              <p>Total: {formatCurrency(order.totalAmount)}</p>
                            </section>
                            <section className="admin-order-details__section">
                              <span className="eyebrow">Delivery details</span>
                              <p>{order.shippingAddress}</p>
                              <p>
                                {order.city}, {order.postalCode}
                              </p>
                              {order.addressSource ? (
                                <p>
                                  Source:{" "}
                                  {order.addressSource
                                    .replace(/_/g, " ")
                                    .toLowerCase()
                                    .replace(/\b\w/g, (character) => character.toUpperCase())}
                                </p>
                              ) : null}
                              {order.addressMapUrl ? (
                                <p>
                                  Google Maps:{" "}
                                  <a href={order.addressMapUrl} target="_blank" rel="noreferrer">
                                    Open current address
                                  </a>
                                </p>
                              ) : null}
                            </section>
                            <section className="admin-order-details__section admin-order-details__section--full">
                              <span className="eyebrow">Items</span>
                              <div className="admin-order-item-list">
                                {order.items.map((item) => (
                                  <div key={`${order.id}-${item.productSlug}`} className="admin-order-item">
                                    <strong>{item.productName}</strong>
                                    <span>{item.quantity} qty</span>
                                    <span>{formatCurrency(item.unitPrice)}</span>
                                  </div>
                                ))}
                              </div>
                            </section>
                          </div>
                        </td>
                      </tr>
                    ) : null}
                  </React.Fragment>
                ))
              ) : (
                <tr>
                  <td colSpan={8}>
                    <div className="admin-empty-note">
                      {loading ? "Loading pending orders..." : "No pending orders right now. Everything has been delivered."}
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </section>
  );
};

export default AdminPendingOrdersPage;
